/**
 * The retired person record address (`/admin/people/{personKey}`), kept so
 * bookmarks, emails and links land (CASE-027). A person record is a visit on
 * People: `/admin/people?visit=person:{id}`, and its access job is
 * `person:{id}/access`. A legacy view maps to its job: `?view=access` to the
 * access job, and the record, student and teaching views to the record. A
 * retired key (`user:{loginId}`, `student:{studentRecordId}`) is read once so
 * the visit names the person id. Every other parameter and the hash survive.
 */
import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useParams } from "react-router";

import { peopleApi } from "@/lib/api/people";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { personVisitPath } from "./person-visit-path";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RETIRED_KEY = /^(?:user|student):[^:/,]+$/;

export function PersonRecordPage() {
  const { personKey = "" } = useParams();
  const location = useLocation();
  const retired = RETIRED_KEY.test(personKey);
  const resolved = useQuery({
    queryKey: ["people", "retired-key", personKey],
    queryFn: () => peopleApi.detail(personKey),
    enabled: retired,
    retry: false,
  });

  if (!personKey) return <Navigate replace to={PROTECTED_ROUTES.admin.people} />;
  // An address that names no person still lands in the visit, which refuses it and names nothing (R1).
  if (UUID.test(personKey) || !retired) return <Navigate replace to={personVisitPath(personKey, location.search, location.hash)} />;
  if (resolved.data) return <Navigate replace to={personVisitPath(resolved.data.key, location.search, location.hash)} />;
  if (resolved.isError) return <Navigate replace to={personVisitPath(personKey, location.search, location.hash)} />;
  return <div className="app-page" aria-busy="true" />;
}
